import { Injectable } from '@angular/core';
import { OperatorTypeEnum } from 'src/app/enums/operatorType.enum';

export interface NestedFilterRule {
    filterKey: string;
    parentProperty: string;
    childProperty: string;
    filterValues: (string | number | boolean)[];
    operator?: number; // OperatorTypeEnum
    isComplexFilter?: boolean;
    linkedChildProperty?: string;
    linkedValue?: string | number | boolean | null;
}

interface ColumnDetails {
    [key: string]: { nestedPath?: string };
}

interface GridViewColumn {
    property?: string;
    name?: string;
    applyNestedFiltering?: boolean;
    nestedPath?: string;
}

@Injectable({ providedIn: 'root' })
export class NestedFilterService {
    private static instance: NestedFilterService;
    private ruleCache = new Map<string, NestedFilterRule[]>();

    constructor() {
        NestedFilterService.instance = this;
    }

    static getInstance(): NestedFilterService {
        return NestedFilterService.instance ??= new NestedFilterService();
    }

    processNestedFilters<T>(data: T[], filterBy: Record<string, unknown>, nestedFilterRules: NestedFilterRule[]): T[] {
        if (!data?.length || !filterBy || !nestedFilterRules?.length) return data;
        
        const activeFilters = this.extractActiveNestedFilters(filterBy, nestedFilterRules);
        if (!activeFilters.length) return data;
        
        return data
            .map(item => this.processItemNestedFilters(item, activeFilters))
            .filter(item => {
                const parentProperties = [...new Set(activeFilters.map(f => f.parentProperty))];
                return parentProperties.some(parentProp => {
                    const nestedArr = (item as Record<string, unknown>)[parentProp];
                    return Array.isArray(nestedArr) && nestedArr.length > 0;
                });
            });
    }

    clearCache(): void {
        this.ruleCache.clear();
    }

    createNestedFilterRules(columnDetails: ColumnDetails, gridViewColumns: GridViewColumn[]): NestedFilterRule[] {
        const cacheKey = JSON.stringify(gridViewColumns.map(c => ({
            name: c.property || c.name,
            applyNestedFiltering: c.applyNestedFiltering,
            nestedPath: c.nestedPath
        })));

        if (this.ruleCache.has(cacheKey)) return this.ruleCache.get(cacheKey)!;

        const rules = gridViewColumns.flatMap(col => {
            if (!col.applyNestedFiltering) return [];
            let nestedPath = col.nestedPath || columnDetails?.[col.property || col.name!]?.nestedPath;
            if (!nestedPath) return [];
            const [p, c] = nestedPath.split('.');
            if (!c) return [];
            
            const childProperty = c === c.toUpperCase() ? c.toLowerCase() : c.charAt(0).toLowerCase() + c.slice(1);
            
            return [{
                filterKey: nestedPath,
                parentProperty: p[0].toLowerCase() + p.slice(1),
                childProperty: childProperty,
                filterValues: []
            }];
        });

        this.ruleCache.set(cacheKey, rules);
        return rules;
    }

    private extractActiveNestedFilters(filterBy: Record<string, unknown>, rules: NestedFilterRule[]): NestedFilterRule[] {
        return rules.flatMap(rule => {
            let value = filterBy[rule.filterKey];
            let isComplexFilter = false;
            let operator: number | undefined;
            let linkedChildProperty: string | undefined;
            let linkedValue: string | number | boolean | null | undefined;

            if (value === undefined || value === null || value === '') {
                const caseInsensitiveKey = Object.keys(filterBy).find(key =>
                    key.toLowerCase() === rule.filterKey.toLowerCase()
                );
                if (caseInsensitiveKey) {
                    value = filterBy[caseInsensitiveKey];
                }
            }

            if (value === undefined || value === null || value === '') {
                const operatorKeys = Object.keys(filterBy).filter(key => {
                    if (!key.startsWith('OperatorTypeEnum.')) return false;

                    const underscoreIndex = key.indexOf('_');
                    if (underscoreIndex === -1) return false;

                    const propertyPart = key.substring(underscoreIndex + 1);

                    if (propertyPart === rule.filterKey) return true;
                    if (propertyPart.toLowerCase() === rule.filterKey.toLowerCase()) return true;

                    return false;
                });

                if (operatorKeys.length > 0) {
                    const operatorKey = operatorKeys[0];
                    const operatorMatch = operatorKey.match(/^OperatorTypeEnum\.(\d+)_/);
                    if (operatorMatch) {
                        operator = parseInt(operatorMatch[1]);
                        const rawValue = filterBy[operatorKey];

                        if (typeof rawValue === 'string') {
                            try {
                                const parsed = JSON.parse(rawValue);
                                if (parsed && parsed.Value !== undefined) {
                                    value = parsed.Value;
                                    isComplexFilter = true;
                                }
                                if (parsed?.LinkedField && parsed?.LinkedValue != null) {
                                    const linkedParts = parsed.LinkedField.split('.');
                                    const rawChild = linkedParts.length > 1 ? linkedParts[linkedParts.length - 1] : linkedParts[0];
                                    linkedChildProperty = rawChild === rawChild.toUpperCase()
                                        ? rawChild.toLowerCase()
                                        : rawChild.charAt(0).toLowerCase() + rawChild.slice(1);
                                    linkedValue = this.parseFilterValue(parsed.LinkedValue);
                                }
                            } catch (e) {
                                value = rawValue;
                                isComplexFilter = true;
                            }
                        } else {
                            value = rawValue;
                            isComplexFilter = true;
                        }
                    }
                }
            }

            if (value === undefined || value === null || value === '') {
                return [];
            }

            const valueArray = Array.isArray(value)
                ? value
                : typeof value === 'string' && value.includes(',')
                    ? value.split(',').map(s => s.trim())
                    : [value];

            const parsedValues = valueArray.map(v => this.parseFilterValue(v)).filter(v => v !== null && v !== '');

            const activeFilter: NestedFilterRule = {
                ...rule,
                filterValues: parsedValues,
                operator: operator,
                isComplexFilter: isComplexFilter,
                linkedChildProperty,
                linkedValue: linkedValue ?? null,
            };

            return parsedValues.length ? [activeFilter] : [];
        });
    }

    private processItemNestedFilters<T>(item: T, activeFilters: NestedFilterRule[]): T {
        const copy = { ...item } as Record<string, unknown>;
        
        const filtersByParent = new Map<string, NestedFilterRule[]>();
        for (const filter of activeFilters) {
            if (!filtersByParent.has(filter.parentProperty)) {
                filtersByParent.set(filter.parentProperty, []);
            }
            filtersByParent.get(filter.parentProperty)!.push(filter);
        }
        
        for (const [parentProperty, parentFilters] of filtersByParent) {
            const nestedArr = copy[parentProperty];
            
            if (!Array.isArray(nestedArr)) continue;

            const filteredArray = nestedArr.filter(nestedItem => {
                return parentFilters.every(filter => {
                    const nestedValue = (nestedItem as Record<string, unknown>)[filter.childProperty] as
                        | string
                        | number
                        | boolean
                        | null
                        | undefined;

                    if (nestedValue == null) return false;

                    if (filter.linkedChildProperty && filter.linkedValue != null) {
                        const linkedActual = this.parseFilterValue(
                            (nestedItem as Record<string, unknown>)[filter.linkedChildProperty]
                        );
                        if (linkedActual == null || linkedActual !== filter.linkedValue) return false;
                    }

                    if (filter.isComplexFilter && filter.operator !== undefined && filter.filterValues.length > 0) {
                        const targetValue = filter.filterValues[0];
                        return this.compareValues(nestedValue, targetValue, filter.operator);
                    }

                    const valueSet = new Set<string | number | boolean>(filter.filterValues);
                    return valueSet.has(nestedValue);
                });
            });

            copy[parentProperty] = filteredArray;
        }

        return copy as T;
    }

    private compareValues(actualValue: string | number | boolean, targetValue: string | number | boolean, operator: number): boolean {
        // Convert to numbers for numeric comparison if both can be converted
        const numActual = Number(actualValue);
        const numTarget = Number(targetValue);
        const bothNumbers = !isNaN(numActual) && !isNaN(numTarget);

        switch (operator) {
            case OperatorTypeEnum.Equal:
                return bothNumbers ? numActual === numTarget : actualValue === targetValue;
            
            case OperatorTypeEnum.LessThan:
                return bothNumbers ? numActual < numTarget : String(actualValue) < String(targetValue);
            
            case OperatorTypeEnum.LessThanOrEqual:
                return bothNumbers ? numActual <= numTarget : String(actualValue) <= String(targetValue);
            
            case OperatorTypeEnum.GreaterThan:
                return bothNumbers ? numActual > numTarget : String(actualValue) > String(targetValue);
            
            case OperatorTypeEnum.GreaterThanOrEqual:
                return bothNumbers ? numActual >= numTarget : String(actualValue) >= String(targetValue);
            
            case OperatorTypeEnum.NotEqual:
                return bothNumbers ? numActual !== numTarget : actualValue !== targetValue;
            
            case OperatorTypeEnum.Contains:
                return String(actualValue).toLowerCase().includes(String(targetValue).toLowerCase());
            
            case OperatorTypeEnum.StartsWith:
                return String(actualValue).toLowerCase().startsWith(String(targetValue).toLowerCase());
            
            default:
                return actualValue === targetValue;
        }
    }


    private parseFilterValue(v: unknown): string | number | boolean | null {
        if (v === null || v === undefined || v === '') return null;
        if (typeof v === 'string') {
            const num = Number(v);
            if (!isNaN(num) && isFinite(num)) return num;
            const l = v.toLowerCase();
            if (l === 'true') return true;
            if (l === 'false') return false;
            return v;
        }
        if (typeof v === 'number' || typeof v === 'boolean') return v;
        return String(v);
    }
}
